import { addDays } from "date-fns";
import { enUS, es, it } from "date-fns/locale";
import type { FC, RefObject, Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import { type Range } from "react-date-range";
import { useTranslation } from "react-i18next";
import { getLocaleLanguage } from "../../../assets/Services/i18next";
import { datetimeToString } from "../../../assets/Utils/generic";
import { LANG } from "../../../config/Lang";
import { ensureInView } from "./utils";
import { format } from "date-fns";

import { cn } from "../../../assets/Utils/generic";
import DatesModal from "./DatesModal";
import { SearchParameters } from "../../../assets/Services/wsSearch";

const DatesInput: FC<{
  mode: "mobile" | "desktop";
  isSmallScreen: boolean;
  isCalendarOpen: boolean;
  setIsCalendarOpen: Dispatch<SetStateAction<boolean>>;
  setIsLocationOpen: Dispatch<SetStateAction<boolean>>;
  setIsGuestsOpen: Dispatch<SetStateAction<boolean>>;
  setParameters: (params: SearchParameters) => void;
  params: SearchParameters;
  calendarInputRef: RefObject<HTMLDivElement>;
  parent: "home" | "nav" | "detail";
}> = ({
  mode,
  isSmallScreen,
  isCalendarOpen,
  setIsCalendarOpen,
  setIsLocationOpen,
  setIsGuestsOpen,
  calendarInputRef,
  params,
  setParameters,
  parent,
}) => {
  const { t } = useTranslation();

  const [shouldOpenOver, setShouldOpenOver] = useState(false);

  const [range, setRange] = useState<Range>({
    startDate: params?.arrivalDate ? new Date(params?.arrivalDate) : new Date(),
    endDate: params?.departureDate
      ? new Date(params?.departureDate)
      : addDays(new Date(), 1),
    key: "selection",
  });

  const calendarRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const input = document.getElementById("datesInput");
    const navH = document.querySelector("nav")?.clientHeight ?? 0;
    if (input) {
      const rect = input.getBoundingClientRect();
      setShouldOpenOver(
        rect.bottom + 366 > window.innerHeight && rect.top - 366 > navH,
      );
    }
  };

  const [locale, setLocale] = useState<Locale>();

  useEffect(() => {
    const initAsync = async () => {
      const l = await getLocaleLanguage();

      let langValue: LANG | undefined = l as LANG | undefined;

      let locale = enUS;
      if (langValue === LANG.it) locale = it;
      if (langValue === LANG.es) locale = es;
      setLocale(locale);

      if (langValue === LANG.en) langValue = "en-gb" as any;

      setParameters({ ...params, language: langValue });
    };
    initAsync();
  }, [t]);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle clicks outside the calendar
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false);
      }
    }

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCalendarOpen]);

  const [calendarLocale, setCalendarLocale] = useState<Locale>();

  useEffect(() => {
    const initAsync = async () => {
      const l = await getLocaleLanguage();

      let langValue: LANG | undefined = l as LANG | undefined;

      let calendarLocale = enUS;
      if (langValue === LANG.it) calendarLocale = it;
      if (langValue === LANG.es) calendarLocale = es;
      setCalendarLocale(calendarLocale);

      if (langValue === LANG.en) langValue = "en-gb" as any;

      setParameters({ ...params, language: langValue });
    };
    initAsync();
  }, [t]);

  return (
    <div
      className="relative"
      style={{
        padding: mode === "mobile" && parent !== "detail" ? "8px 24px" : "0",
        ...(parent !== "nav"
          ? {
              width: "100%",
              flexGrow: 1,
              flexBasis: 0,
              minWidth: mode === "desktop" ? "auto" : 0,
            }
          : {}),
      }}
      ref={calendarInputRef}
    >
      <div
        id="datesInput"
        onClick={() => {
          setIsCalendarOpen(!isCalendarOpen);

          setTimeout(() => {
            if (parent !== "nav" && calendarRef.current) {
              ensureInView(calendarRef);
            }
          }, 0);
        }}
        className="d-flex gap-2 cursor-pointer h100 align-items-center"
      >
        {parent === "detail" ? (
          <div className="flex w-full gap-1">
            <div className="flex flex-col w-1/2 border border-[#E8E8E8] p-2 rounded-xl justify-center">
              <p className="text-xs leading-4 light dark-grey-color">{t("ds.arrival")}</p>
              <p className="text-sm leading-4">
                {params.arrivalDate ? `${format(new Date(params.arrivalDate), "iii dd MMM", {
                    locale
                })}` : "-"}
              </p>
            </div>
            <div className="flex flex-col w-1/2 border border-[#E8E8E8] p-2 rounded-xl justify-center">
              <p className="text-xs leading-4 light dark-grey-color">
                {t("ds.departure")}
              </p>
              <p className="text-sm leading-4">
                {params.departureDate
                  ? `${format(new Date(params.departureDate), "iii dd MMM", {
                    locale
                  })}` : "-"}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile */}
            <div className="flex flex-col gap-1 w-full md:hidden">
              {parent !== "nav" ? (
                <span className={"fs-body-sm dark-grey-color"}>
                  {t("ds.data-label")}
                </span>
              ) : null}
              <p className={`fs-body medium text-nowrap`}>
                {(params.arrivalDate && params.departureDate) ? `${format(new Date(params.arrivalDate), "iii dd MMM", {
                    locale
                  })} - ${format(new Date(params.departureDate), "iii dd MMM", {
                    locale
                  })}` : t("ds.checkin_ph")}
              </p>
            </div>
            {/* Desktop */}
            <div className="gap-2 w-full hidden md:flex">
              <div
                data-focused={isCalendarOpen}
                className={cn(
                  "d-flex gap-2 w100 h100 lg:py-2 px-3 rounded-2xl data-[focused=true]:bg-[#F8F8F8] hover:bg-[#F8F8F8] transition-colors duration-300",
                  parent === "nav" && "data-[focused=true]:bg-transparent",
                )}
              >
                <div className="d-flex flex-column gap-1">
                  {parent !== "nav" && (
                    <span className={"fs-body-sm light dark-grey-color"}>
                      {t("ds.data-label")}
                    </span>
                  )}
                  <p
                    style={{
                      fontSize: mode === "mobile" ? "16px !important" : "",
                    }}
                    className={cn(
                      "medium text-nowrap",
                      mode === "desktop" ? "fs-body" : "fs-body",
                      parent === "nav" && "text-sm",
                    )}
                  >
                  {(params.arrivalDate && params.departureDate) ? `${format(new Date(params.arrivalDate), "iii dd MMM", {
                    locale
                  })} - ${format(new Date(params.departureDate), "iii dd MMM", {
                    locale
                  })}` : t("ds.checkin_ph")}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <DatesModal
        isCalendarOpen={isCalendarOpen}
        calendarRef={calendarRef}
        mode={mode}
        shouldOpenOver={shouldOpenOver}
        setIsCalendarOpen={setIsCalendarOpen}
        calendarLocale={calendarLocale}
        range={range}
        setRange={setRange}
        setIsLocationOpen={setIsLocationOpen}
        setIsGuestsOpen={setIsGuestsOpen}
        parent={parent}
        searchParameters={params}
        setParameters={setParameters}
      />
    </div>
  );
};

export default DatesInput;
